import Microgame from '../engine/Microgame.js';

export default class ReelGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Mouline !`, en: `Reel!`, es: `¡Carrete!` },
            controls: { fr: `Fais tourner ton doigt/souris autour du centre`, en: `Spin your finger/mouse around the center`, es: `Gira tu dedo/ratón alrededor del centro` },
            objective: { fr: `Remonte la ligne le plus vite possible`, en: `Reel the line back in as fast as possible`, es: `Enrolla la línea lo más rápido posible` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color)" stroke-width="2"><circle cx="12" cy="12" r="8"/><path d="M12 8v4l3 3"/></svg>`,
            actions: [
                { type: 'swipe', fromX: 50, fromY: 32, toX: 70, toY: 50, duration: 200 },
                { type: 'swipe', fromX: 70, fromY: 50, toX: 50, toY: 68, duration: 200 },
                { type: 'swipe', fromX: 50, fromY: 68, toX: 30, toY: 50, duration: 200 },
                { type: 'swipe', fromX: 30, fromY: 50, toX: 50, toY: 32, duration: 200 },
                { type: 'swipe', fromX: 50, fromY: 32, toX: 70, toY: 50, duration: 200 },
                { type: 'swipe', fromX: 70, fromY: 50, toX: 50, toY: 68, duration: 200 },
                { type: 'wait', duration: 320 },
            ],
            loopDuration: 3000
        };
    }
    constructor(engine) {
        super(engine);
        this.progress = 0;
        this.isSwiping = false;
        this.lastX = 0;
        this.lastY = 0;
        this.reelAngle = 0;

        const speedMult = engine.speedMultiplier || 1.0;
        this.reelSensitivity = 0.18;
        this.fishFightSpeed = 8 + (speedMult - 1) * 4;

        // Reduce time at max speed by ~100ms
        const adjustedSpeed = 1 + (speedMult - 1) * (3.1 / 3);
        this.duration = 3000 * (speedMult / adjustedSpeed);
    }

    createFishSVG() {
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('viewBox', '0 0 80 40');
        svg.setAttribute('width', '60');
        svg.setAttribute('height', '30');
        svg.style.overflow = 'visible';

        // Body
        const body = document.createElementNS('http://www.w3.org/2000/svg', 'ellipse');
        body.setAttribute('cx', '35');
        body.setAttribute('cy', '20');
        body.setAttribute('rx', '22');
        body.setAttribute('ry', '12');
        body.setAttribute('fill', this.secondaryColor);
        body.setAttribute('opacity', '0.9');
        svg.appendChild(body);

        // Tail
        const tail = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
        tail.setAttribute('points', '58,20 75,6 75,34');
        tail.setAttribute('fill', this.primaryColor);
        tail.setAttribute('opacity', '0.8');
        svg.appendChild(tail);

        // Dorsal fin
        const dorsal = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
        dorsal.setAttribute('points', '28,8 40,2 42,8');
        dorsal.setAttribute('fill', this.primaryColor);
        dorsal.setAttribute('opacity', '0.7');
        svg.appendChild(dorsal);

        // Eye
        const eye = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        eye.setAttribute('cx', '22');
        eye.setAttribute('cy', '17');
        eye.setAttribute('r', '3');
        eye.setAttribute('fill', this.primaryColor);
        svg.appendChild(eye);

        // Pupil
        const pupil = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        pupil.setAttribute('cx', '21');
        pupil.setAttribute('cy', '17');
        pupil.setAttribute('r', '1.5');
        pupil.setAttribute('fill', '#000');
        svg.appendChild(pupil);

        return svg;
    }

    setup() {
        this.engine.ui.instruction.textContent = "REEL!";

        const w = this.container.clientWidth;
        const h = this.container.clientHeight;

        // Water background
        const water = document.createElement('div');
        water.style.position = 'absolute';
        water.style.bottom = '0';
        water.style.left = '0';
        water.style.width = '100%';
        water.style.height = '75%';
        water.style.background = `linear-gradient(180deg, ${this.secondaryColor}15 0%, ${this.secondaryColor}30 100%)`;
        water.style.borderTop = `2px solid ${this.secondaryColor}`;
        this.container.appendChild(water);

        // Water surface waves
        const waves = document.createElement('div');
        waves.style.position = 'absolute';
        waves.style.top = '25%';
        waves.style.left = '0';
        waves.style.width = '100%';
        waves.style.height = '6px';
        waves.style.opacity = '0.4';
        waves.style.background = `repeating-linear-gradient(90deg, transparent 0px, ${this.secondaryColor} 15px, transparent 30px)`;
        this.container.appendChild(waves);

        // Fishing line
        this.lineEl = document.createElement('div');
        this.lineEl.style.position = 'absolute';
        this.lineEl.style.width = '2px';
        this.lineEl.style.background = this.secondaryColor;
        this.lineEl.style.opacity = '0.5';
        this.lineEl.style.left = `${w * 0.5}px`;
        this.lineEl.style.top = '0';
        this.lineEl.style.transformOrigin = 'top center';
        this.container.appendChild(this.lineEl);

        // Fish (SVG)
        this.fishEl = document.createElement('div');
        this.fishEl.style.position = 'absolute';
        this.fishEl.style.left = '50%';
        this.fishEl.style.transform = 'translateX(-50%)';
        this.fishEl.style.filter = `drop-shadow(0 0 6px ${this.secondaryColor})`;
        this.fishEl.appendChild(this.createFishSVG());
        this.container.appendChild(this.fishEl);

        // -- Realistic Reel --
        this.reelContainer = document.createElement('div');
        this.reelContainer.style.position = 'absolute';
        this.reelContainer.style.bottom = '12px';
        this.reelContainer.style.right = '15px';
        this.reelContainer.style.width = '80px';
        this.reelContainer.style.height = '80px';

        // Outer ring
        const outerRing = document.createElement('div');
        outerRing.style.width = '80px';
        outerRing.style.height = '80px';
        outerRing.style.borderRadius = '50%';
        outerRing.style.border = `3px solid ${this.secondaryColor}`;
        outerRing.style.boxShadow = `0 0 10px ${this.secondaryColor}40, inset 0 0 10px ${this.secondaryColor}20`;
        outerRing.style.position = 'absolute';
        this.reelContainer.appendChild(outerRing);

        // Inner spool
        const innerSpool = document.createElement('div');
        innerSpool.style.width = '40px';
        innerSpool.style.height = '40px';
        innerSpool.style.borderRadius = '50%';
        innerSpool.style.border = `2px solid ${this.primaryColor}`;
        innerSpool.style.position = 'absolute';
        innerSpool.style.top = '50%';
        innerSpool.style.left = '50%';
        innerSpool.style.transform = 'translate(-50%, -50%)';
        innerSpool.style.boxShadow = `0 0 8px ${this.primaryColor}40`;
        this.reelContainer.appendChild(innerSpool);

        // Spool lines (cross pattern to show rotation)
        this.spoolLines = document.createElement('div');
        this.spoolLines.style.position = 'absolute';
        this.spoolLines.style.width = '40px';
        this.spoolLines.style.height = '40px';
        this.spoolLines.style.top = '50%';
        this.spoolLines.style.left = '50%';
        this.spoolLines.style.transform = 'translate(-50%, -50%)';

        for (let i = 0; i < 4; i++) {
            const spoke = document.createElement('div');
            spoke.style.position = 'absolute';
            spoke.style.width = '2px';
            spoke.style.height = '36px';
            spoke.style.background = this.primaryColor;
            spoke.style.opacity = '0.5';
            spoke.style.top = '50%';
            spoke.style.left = '50%';
            spoke.style.transformOrigin = '50% 0';
            spoke.style.transform = `translate(-50%, 0) rotate(${i * 45}deg)`;
            this.spoolLines.appendChild(spoke);
        }
        this.reelContainer.appendChild(this.spoolLines);

        // Center dot
        const centerDot = document.createElement('div');
        centerDot.style.width = '10px';
        centerDot.style.height = '10px';
        centerDot.style.borderRadius = '50%';
        centerDot.style.background = this.primaryColor;
        centerDot.style.boxShadow = `0 0 6px ${this.primaryColor}`;
        centerDot.style.position = 'absolute';
        centerDot.style.top = '50%';
        centerDot.style.left = '50%';
        centerDot.style.transform = 'translate(-50%, -50%)';
        centerDot.style.zIndex = '2';
        this.reelContainer.appendChild(centerDot);

        // Handle arm
        this.handleArm = document.createElement('div');
        this.handleArm.style.position = 'absolute';
        this.handleArm.style.width = '30px';
        this.handleArm.style.height = '3px';
        this.handleArm.style.background = this.secondaryColor;
        this.handleArm.style.top = '50%';
        this.handleArm.style.left = '50%';
        this.handleArm.style.transformOrigin = '0 50%';
        this.handleArm.style.transform = 'rotate(0deg)';
        this.reelContainer.appendChild(this.handleArm);

        // Handle knob
        this.handleKnob = document.createElement('div');
        this.handleKnob.style.position = 'absolute';
        this.handleKnob.style.width = '14px';
        this.handleKnob.style.height = '14px';
        this.handleKnob.style.borderRadius = '50%';
        this.handleKnob.style.background = this.primaryColor;
        this.handleKnob.style.boxShadow = `0 0 8px ${this.primaryColor}`;
        this.handleKnob.style.right = '-7px';
        this.handleKnob.style.top = '-5.5px';
        this.handleArm.appendChild(this.handleKnob);

        this.container.appendChild(this.reelContainer);

        this.onDown = this.onDown.bind(this);
        this.onMove = this.onMove.bind(this);
        this.onUp = this.onUp.bind(this);

        this.engine.input.element.addEventListener('pointerdown', this.onDown);
        this.engine.input.element.addEventListener('pointermove', this.onMove);
        this.engine.input.element.addEventListener('pointerup', this.onUp);
    }

    onDown(e) {
        if (!this.active) return;
        this.isSwiping = true;
        this.lastX = e.clientX;
        this.lastY = e.clientY;
    }

    onMove(e) {
        if (!this.active || !this.isSwiping) return;

        const dx = e.clientX - this.lastX;
        const dy = e.clientY - this.lastY;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist > 1) {
            const cappedDist = Math.min(dist, 60);
            this.progress += cappedDist * this.reelSensitivity;
            this.reelAngle += cappedDist * 4;
            if (this.progress > 100) this.progress = 100;
        }

        this.lastX = e.clientX;
        this.lastY = e.clientY;
    }

    onUp() {
        this.isSwiping = false;
    }

    update(dt) {
        if (!this.active) return;

        // Fish fights back when not reeling
        if (!this.isSwiping) {
            this.progress -= this.fishFightSpeed * (dt / 1000);
            if (this.progress < 0) this.progress = 0;
        }

        // Reel rotation (handle arm + spool spokes)
        this.handleArm.style.transform = `rotate(${this.reelAngle}deg)`;
        this.spoolLines.style.transform = `translate(-50%, -50%) rotate(${this.reelAngle}deg)`;

        // Fish position
        const h = this.container.clientHeight;
        const fishStartY = h * 0.85;
        const fishEndY = h * 0.22;
        const fishY = fishStartY - (this.progress / 100) * (fishStartY - fishEndY);
        this.fishEl.style.top = `${fishY}px`;

        // Fish wobble
        const wobble = Math.sin(Date.now() / 150) * 6;
        this.fishEl.style.transform = `translateX(calc(-50% + ${wobble}px))`;

        // Fishing line height
        const fishCenterY = fishY + 15;
        this.lineEl.style.height = `${Math.max(0, fishCenterY)}px`;

        // Win
        if (this.progress >= 100) {
            this.fishEl.style.filter = `drop-shadow(0 0 20px ${this.primaryColor})`;
            this.win();
        }
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointerdown', this.onDown);
        this.engine.input.element.removeEventListener('pointermove', this.onMove);
        this.engine.input.element.removeEventListener('pointerup', this.onUp);
    }
}
